<template>
  <div
    class="accruals-column-tags-input"
    :data-testid="dataTestid"
  >
    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      :disabled="disabled"
      offset-y
      nudge-bottom="4"
      :max-width="menuWidth"
      :min-width="menuWidth"
      content-class="accruals-column-tags-input__menu"
      :data-testid="`${dataTestid}__menu`"
    >
      <template #activator="{ on, attrs }">
        <div
          ref="activator"
          class="accruals-column-tags-input__field"
          :class="{ 'accruals-column-tags-input__field--disabled': disabled }"
          :data-testid="`${dataTestid}__field`"
          role="button"
          tabindex="0"
          :aria-disabled="disabled"
          v-bind="attrs"
          v-on="on"
          @keydown="onActivatorKeydown($event, on)"
        >
          <div class="accruals-column-tags-input__chips">
            <span
              v-for="item in value"
              :key="item"
              class="accruals-column-tags-input__chip"
              :data-testid="`${dataTestid}__chip--${item}`"
            >
              <span class="accruals-column-tags-input__chip-text">{{ item }}</span>
              <button
                v-if="!disabled"
                type="button"
                class="accruals-column-tags-input__chip-remove"
                :aria-label="`Remove ${item}`"
                :data-testid="`${dataTestid}__chip-remove--${item}`"
                @click.stop="removeChip(item)"
              >
                <img
                  src="/img/icons/fbc-icons/ChipClose.svg"
                  alt=""
                  aria-hidden="true"
                  class="accruals-column-tags-input__chip-remove-icon"
                />
              </button>
            </span>
          </div>
          <v-icon class="accruals-column-tags-input__chevron" small>mdi-menu-down</v-icon>
        </div>
      </template>

      <div class="accruals-column-tags-input__dropdown">
        <div class="accruals-column-tags-input__search-wrap">
          <v-text-field
            ref="searchInput"
            v-model="searchQuery"
            dense
            outlined
            hide-details
            placeholder="Search"
            class="accruals-column-tags-input__search"
            :data-testid="`${dataTestid}__search`"
            @click.stop
            @mousedown.stop
          >
            <template #append>
              <img
                src="/img/icons/fbc-icons/SearchIcon.svg"
                alt=""
                aria-hidden="true"
                class="accruals-column-tags-input__search-icon"
              />
            </template>
          </v-text-field>
        </div>

        <div class="accruals-column-tags-input__list" :data-testid="`${dataTestid}__list`">
          <button
            type="button"
            class="accruals-column-tags-input__row"
            :class="{ 'accruals-column-tags-input__row--highlight': isAllSelected }"
            :data-testid="`${dataTestid}__option--all`"
            @click="toggleAll"
          >
            <img
              :src="isAllSelected ? checkedIcon : uncheckedIcon"
              alt=""
              aria-hidden="true"
              class="accruals-column-tags-input__checkbox"
            />
            <span class="accruals-column-tags-input__row-label accruals-column-tags-input__row-label--all">
              All
            </span>
          </button>

          <button
            v-for="item in filteredItems"
            :key="item"
            type="button"
            class="accruals-column-tags-input__row"
            :class="{ 'accruals-column-tags-input__row--highlight': isSelected(item) }"
            :data-testid="`${dataTestid}__option--${item}`"
            @click="toggleItem(item)"
          >
            <img
              :src="isSelected(item) ? checkedIcon : uncheckedIcon"
              alt=""
              aria-hidden="true"
              class="accruals-column-tags-input__checkbox"
            />
            <span class="accruals-column-tags-input__row-label">{{ item }}</span>
          </button>
        </div>

        <div class="accruals-column-tags-input__footer">
          <v-btn
            class="accruals-column-tags-input__clear-btn secondaryBtn"
            :data-testid="`${dataTestid}__clear-btn`"
            @click="clearStaging"
          >
            Clear
          </v-btn>
          <v-btn
            class="accruals-column-tags-input__add-btn primaryBtn"
            :data-testid="`${dataTestid}__add-btn`"
            @click="applyStaging"
          >
            Add
          </v-btn>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
const CHECKED_ICON = '/img/icons/fbc-icons/CheckboxFill.svg';
const UNCHECKED_ICON = '/img/icons/fbc-icons/CheckboxEmpty.svg';

export default {
  name: 'AccrualsColumnTagsInput',
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dataTestid: {
      type: String,
      default: 'accruals-column-tags-input',
    },
  },
  data() {
    return {
      menuOpen: false,
      menuWidth: 0,
      searchQuery: '',
      stagingSelection: [],
      checkedIcon: CHECKED_ICON,
      uncheckedIcon: UNCHECKED_ICON,
    };
  },
  computed: {
    filteredItems() {
      const query = (this.searchQuery || '').trim().toLowerCase();
      const items = this.items || [];
      if (!query) return items;
      return items.filter((item) => String(item).toLowerCase().includes(query));
    },
    isAllSelected() {
      const items = this.filteredItems;
      if (!items.length) return false;
      return items.every((item) => this.stagingSelection.includes(item));
    },
  },
  watch: {
    menuOpen(isOpen) {
      if (isOpen) {
        this.updateMenuWidth();
        this.stagingSelection = [...(this.value || [])];
        this.searchQuery = '';
        this.$nextTick(() => {
          const input = this.$refs.searchInput?.$refs?.input
            || this.$refs.searchInput?.$el?.querySelector?.('input');
          input?.focus();
        });
        return;
      }
      this.searchQuery = '';
    },
  },
  beforeDestroy() {
    this.menuOpen = false;
    this.stagingSelection = [];
    this.searchQuery = '';
  },
  methods: {
    onActivatorKeydown(event, on) {
      if (this.disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        on.click(event);
      }
    },
    updateMenuWidth() {
      const field = this.$refs.activator;
      if (!field) return;
      this.menuWidth = field.getBoundingClientRect().width;
    },
    isSelected(item) {
      return this.stagingSelection.includes(item);
    },
    toggleItem(item) {
      if (this.isSelected(item)) {
        this.stagingSelection = this.stagingSelection.filter((value) => value !== item);
        return;
      }
      this.stagingSelection = [...this.stagingSelection, item];
    },
    toggleAll() {
      if (this.isAllSelected) {
        const filtered = new Set(this.filteredItems);
        this.stagingSelection = this.stagingSelection.filter((item) => !filtered.has(item));
        return;
      }
      const merged = new Set([...this.stagingSelection, ...this.filteredItems]);
      this.stagingSelection = Array.from(merged);
    },
    clearStaging() {
      this.stagingSelection = [];
    },
    applyStaging() {
      const selected = new Set(this.stagingSelection);
      const next = (this.items || []).filter((item) => selected.has(item));
      this.$emit('input', next);
      this.$emit('change', next);
      this.menuOpen = false;
    },
    removeChip(item) {
      const next = (this.value || []).filter((value) => value !== item);
      this.$emit('input', next);
      this.$emit('change', next);
    },
  },
};
</script>

<style lang="scss" scoped src="./AccrualsColumnTagsInput.scss"></style>
